import {
    Divider,
    FormControl,
    InputLabel,
    ListItemText,
    MenuItem,
    Select,
    SelectChangeEvent,
    Stack,
} from '@mui/material';
import { find, lowerCase, map, upperFirst } from 'lodash';
import { ReactNode, useCallback, useId, useState } from 'react';

import { sensorToColor } from '@/charts/common';
import { ColorSet } from '@/charts/types';
import { ColorIcon } from '@/components/data/ColorIcon';

const ALL_LINES = 'ALL_LINES';

const defaultRenderValues: ChartLineSelectorProps['renderValues'] = ({ values, lines }) =>
    values
        .map((id) => find(lines, { id }))
        .map((l) => l?.selectorLabel ?? l?.sensorLabel ?? l?.label)
        .join(', ');

export type ChartLineSelectorProps = {
    label?: string;
    allLinesLabel: ReactNode;
    lines: { id: string; label: string; selectorLabel?: string; sensorLabel?: string; color: ColorSet }[];
    onChange: (activeLineIds: string[]) => void;
    renderValues?: ({ values, lines }: { values: string[]; lines: ChartLineSelectorProps['lines'] }) => string;
};

export const ChartLineSelector = ({ label, lines, allLinesLabel, renderValues, onChange }: ChartLineSelectorProps) => {
    const id = `ChartLineSelector-${useId()}`;
    const allLineIds = map(lines, 'id');
    const [open, setOpen] = useState(false);
    const [activeOptionIds, setActiveOptionIds] = useState<string[]>([ALL_LINES]);
    const [isAllLines, setIsAllLines] = useState(true);

    const handleChange = useCallback(
        ({ target }: SelectChangeEvent<string[]>) => {
            let value = typeof target.value === 'string' ? target.value.split(',') : target.value;
            if (isAllLines && value.filter((id) => id !== ALL_LINES).length) {
                value = value.filter((id) => id !== ALL_LINES);
            }
            if (value.includes(ALL_LINES)) {
                setActiveOptionIds([ALL_LINES]);
                onChange(allLineIds);
                setIsAllLines(true);
                setOpen(false);
            } else if (!value.length) {
                onChange(allLineIds);
                setActiveOptionIds([]);
                setIsAllLines(true);
            } else {
                // not just using `value` directly so that we maintain line order in the label
                const lineIds = map(
                    lines.filter((l) => value.includes(l.id)),
                    'id'
                );
                setActiveOptionIds(lineIds);
                onChange(lineIds);
                setIsAllLines(false);
            }
        },
        [allLineIds, isAllLines, lines, onChange]
    );

    const handleClickAllLines = () => {
        setTimeout(() => {
            // setting timeout to do this all after the initial item clearing click has completed, if we were already selected.
            // no idea why preventDefault or stopPropagation don't prevent that
            setActiveOptionIds([ALL_LINES]);
            setIsAllLines(true);
            setOpen(false);
        });
    };

    const handleClose = useCallback(() => {
        if (activeOptionIds.length === lines.length || activeOptionIds.length === 0) {
            setActiveOptionIds([ALL_LINES]);
            setIsAllLines(true);
        }
        setOpen(false);
    }, [activeOptionIds.length, lines.length]);

    if (lines.length < 2) {
        return false;
    }

    return (
        <FormControl>
            <InputLabel id={`${id}-label`}>{label}</InputLabel>
            <Select
                labelId={`${id}-label`}
                id={id}
                multiple
                size="small"
                value={activeOptionIds}
                open={open}
                onClose={handleClose}
                onOpen={() => setOpen(true)}
                onChange={handleChange}
                variant="outlined"
                renderValue={(selected) =>
                    isAllLines ? allLinesLabel : (renderValues ?? defaultRenderValues)({ values: selected, lines })
                }
            >
                <MenuItem value={ALL_LINES} onClick={handleClickAllLines}>
                    <ListItemText primary={allLinesLabel} />
                </MenuItem>
                <Divider sx={{ m: '2px' }} />
                {lines.map(({ id, label, sensorLabel, selectorLabel, color }) => (
                    <MenuItem key={id} value={id}>
                        <Stack direction="row" sx={{ gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
                            <ColorIcon color={color.line} />
                            <ListItemText primary={selectorLabel ?? sensorLabel ?? label} />
                        </Stack>
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
};

export type ChartSensorSelectorProps = {
    sensorPositions: string[];
    onChange: (activeLineIds: string[]) => void;
};

export const ChartSensorSelector = ({ sensorPositions, onChange }: ChartSensorSelectorProps) => {
    if (sensorPositions.length <= 1) {
        return false;
    }
    return (
        <ChartLineSelector
            lines={sensorPositions.map((id) => ({
                id,
                label: upperFirst(lowerCase(id)),
                color: sensorToColor(id),
            }))}
            allLinesLabel="All sensors"
            onChange={onChange}
            renderValues={({ values }) =>
                values.length === 1
                    ? `Sensor ${values[0].slice(-1)}`
                    : `Sensors ${values.map((id) => id.slice(-1)).join(', ')}`
            }
        />
    );
};
